/**
 * A link from a screen into the article that explains it.
 *
 * Renders nothing when HELPDESK is off, so a call site needs no flag check of
 * its own and adding one is a one-line change. The article id is not validated
 * here — an id that no longer exists lands on the helpdesk overview rather
 * than on an error, which is the right outcome for a help link.
 */

import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { IconHelpCircle } from "@tabler/icons-react"
import { Link } from "react-router"
import { helpArticlePath } from "./routes"

interface HelpdeskLinkProps {
  categoryId: string
  articleId: string
  /** Defaults to "Learn more". Pass a specific label where the context needs one. */
  label?: string
  className?: string
}

export function HelpdeskLink({
  categoryId,
  articleId,
  label,
  className,
}: HelpdeskLinkProps) {
  const { translate } = useTranslation()
  const helpCenterEnabled = useFeatureFlag("HELPDESK")
  if (!helpCenterEnabled) return null

  return (
    <Link
      to={helpArticlePath(categoryId, articleId)}
      className={cn(
        "inline-flex items-center gap-1 text-xs text-grey5 transition-colors hover:text-yellow1",
        className,
      )}
      data-testid={`help-link-${articleId}`}
    >
      <IconHelpCircle size={14} stroke={1.75} />
      {label ?? translate("HELPDESK/LEARN_MORE")}
    </Link>
  )
}
